<template>
    <section class="dp-layout blackboard-entry-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="filter-bar">
                    <el-form :inline="true" size="small">
                        <el-form-item label="年月">
                            <el-date-picker
                                v-model="filterForm.yearMonth"
                                type="month"
                                placeholder="选择月份"
                                value-format="yyyy-MM"
                                style="width: 140px;"
                                @change="handleSearch"
                            />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                        </el-form-item>
                        <div v-if="scopeParts.length" class="scope-bar">
                            <el-tag
                                v-for="(p, i) in scopeParts"
                                :key="i"
                                size="small"
                                :type="p.type || 'info'"
                                effect="plain"
                                class="scope-tag"
                            >{{ p.label }}</el-tag>
                        </div>
                    </el-form>
                </div>

                <el-alert
                    title="列出所选范围内的全部班级。还没上传的也会显示，照片列为「未上传」。"
                    type="info"
                    :closable="false"
                    show-icon
                    class="tip-alert"
                />

                <div class="tbl-scroll-host">
                    <el-table :data="list" v-loading="loading" border height="100%" class="compact-table">
                        <el-table-column prop="CampusName" label="校区" min-width="110" show-overflow-tooltip />
                        <el-table-column prop="GradeName" label="年级" min-width="100" show-overflow-tooltip />
                        <el-table-column prop="ClassName" label="班级" min-width="120" show-overflow-tooltip />
                        <el-table-column label="照片" min-width="200">
                            <template slot-scope="scope">
                                <div v-if="scope.row.Photos && scope.row.Photos.length" class="photo-row">
                                    <oss-el-image
                                        v-for="(src, i) in scope.row.Photos.slice(0, 4)"
                                        :key="i"
                                        class="thumb"
                                        :src="src"
                                        :school-id="schoolId"
                                        :preview-src-list="scope.row.Photos"
                                        fit="cover"
                                    />
                                    <span v-if="scope.row.Photos.length > 4" class="muted">+{{ scope.row.Photos.length - 4 }}</span>
                                </div>
                                <span v-else class="muted">未上传</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="UploaderName" label="上传人" width="110" show-overflow-tooltip>
                            <template slot-scope="scope">{{ scope.row.UploaderName || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="上传时间" width="160" show-overflow-tooltip>
                            <template slot-scope="scope">{{ formatUploadTime(scope.row.UploadTime) }}</template>
                        </el-table-column>
                        <el-table-column label="结算状态" width="110" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="settleTagType(scope.row.SettleStatus)" size="mini">
                                    {{ settleText(scope.row.SettleStatus) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div class="pager">
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next"
                        :current-page="page"
                        :page-size="pageSize"
                        :page-sizes="pageSizes"
                        :total="total"
                        @current-change="onPageChange"
                        @size-change="onPageSizeChange"
                    />
                </div>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-picture-outline"></i>
                    <p>请在顶部选择学校，并在左侧选择部门</p>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import OssElImage from '../../components/OssElImage';
import { getBlackboardEntryPage } from '../../api/modules/blackboard';
import { normalizeDeptId, treeNodeDeptId, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'BlackboardEntry',
    components: { DepartmentPane, OssElImage },
    data() {
        const now = new Date();
        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            scopeParts: [],
            filterForm: {
                yearMonth: ym,
                campusId: 0,
                gradeId: 0,
                scopeDeptId: 0,
                classId: 0
            },
            list: [],
            loading: false,
            page: 1,
            pageSize: 20,
            pageSizes: [10, 20, 50, 100],
            total: 0
        };
    },
    created() {
        const sid = this.resolveSchoolId();
        if (sid) this.bindSchool(sid, this.$store.state.activeSchoolName);
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.bindSchool(newSchoolId, this.$store.state.activeSchoolName);
            this.resetScope();
            this.handleSearch();
        }
    },
    methods: {
        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },
        bindSchool(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
        },
        resetScope() {
            this.filterForm.campusId = 0;
            this.filterForm.gradeId = 0;
            this.filterForm.scopeDeptId = 0;
            this.filterForm.classId = 0;
            this.scopeParts = [];
            this.list = [];
            this.total = 0;
        },
        orgTypeOf(data) {
            return String((data && (data.DeptOrgType || data.deptOrgType)) || '').toLowerCase();
        },
        findAncestor(node, orgType) {
            let cur = node;
            const want = String(orgType || '').toLowerCase();
            while (cur) {
                const d = cur.data || cur;
                if (this.orgTypeOf(d) === want) {
                    return {
                        id: treeNodeDeptId(d),
                        name: d.label || d.Name || d.name || ''
                    };
                }
                cur = cur.parent;
            }
            return { id: '', name: '' };
        },
        /** 左侧教学树：校区/年级/班级映射到筛选。创建于 2026-09-24 */
        treeNodeClick(data, node) {
            if (!data) {
                this.resetScope();
                return;
            }
            const sid = this.resolveSchoolId();
            if (sid) this.bindSchool(sid, this.$store.state.activeSchoolName);

            const orgType = this.orgTypeOf(data);
            const id = treeNodeDeptId(data);
            const label = data.label || data.Name || data.name || '';

            this.filterForm.campusId = 0;
            this.filterForm.gradeId = 0;
            this.filterForm.scopeDeptId = 0;
            this.filterForm.classId = 0;
            this.scopeParts = [];

            if (orgType === 'period') {
                const campus = this.findAncestor(node, 'campus');
                this.filterForm.scopeDeptId = id;
                this.filterForm.campusId = campus.id;
                this.scopeParts = [
                    ...(campus.name ? [{ label: campus.name, type: '' }] : []),
                    { label: label || '学段', type: 'warning' }
                ];
            } else if (orgType === 'campus') {
                this.filterForm.campusId = id;
                this.scopeParts = [{ label: label || '校区', type: '' }];
            } else if (orgType === 'grade') {
                const campus = this.findAncestor(node, 'campus');
                this.filterForm.gradeId = id;
                this.filterForm.campusId = campus.id;
                this.scopeParts = [
                    ...(campus.name ? [{ label: campus.name, type: '' }] : []),
                    { label: label || '年级', type: 'success' }
                ];
            } else if (orgType === 'class') {
                const grade = this.findAncestor(node, 'grade');
                const campus = this.findAncestor(node, 'campus');
                this.filterForm.classId = id;
                this.filterForm.gradeId = grade.id;
                this.filterForm.campusId = campus.id;
                this.scopeParts = [
                    ...(campus.name ? [{ label: campus.name, type: '' }] : []),
                    ...(grade.name ? [{ label: grade.name, type: 'success' }] : []),
                    { label: label || '班级', type: 'warning' }
                ];
            } else if (orgType === 'school' || orgType === 'affiliateschool') {
                this.scopeParts = [{ label: label || '全校', type: '' }];
            } else if (label) {
                this.scopeParts = [{ label, type: 'info' }];
            }
            this.handleSearch();
        },
        handleSearch() {
            this.page = 1;
            this.loadList();
        },
        onPageChange(p) {
            this.page = p;
            this.loadList();
        },
        onPageSizeChange(size) {
            this.pageSize = size;
            this.page = 1;
            this.loadList();
        },
        async loadList() {
            if (!this.schoolId || !this.filterForm.yearMonth) {
                this.list = [];
                this.total = 0;
                return;
            }
            this.loading = true;
            try {
                const res = await getBlackboardEntryPage({
                    schoolId: this.schoolId,
                    yearMonth: this.filterForm.yearMonth,
                    campusId: this.filterForm.campusId || 0,
                    gradeId: this.filterForm.gradeId || 0,
                    scopeDeptId: this.filterForm.scopeDeptId || 0,
                    classId: this.filterForm.classId || 0,
                    page: this.page,
                    intPageSize: this.pageSize
                });
                if (res.data && res.data.success) {
                    const pageData = res.data.response || {};
                    this.list = pageData.data || [];
                    this.total = pageData.dataCount || 0;
                } else {
                    this.list = [];
                    this.total = 0;
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            } catch (e) {
                this.list = [];
                this.total = 0;
                this.$message.error('加载失败');
            } finally {
                this.loading = false;
            }
        },
        formatUploadTime(value) {
            if (!value) return '-';
            const text = String(value).replace('T', ' ');
            return text.length >= 16 ? text.slice(0, 16) : text;
        },
        settleText(status) {
            if (status === 'Settled') return '已写入';
            if (status === 'Revoked') return '已撤销';
            if (status === 'Failed') return '失败';
            if (status === 'Skipped') return '跳过';
            return '未结算';
        },
        settleTagType(status) {
            if (status === 'Settled') return 'success';
            if (status === 'Revoked') return 'info';
            if (status === 'Failed') return 'danger';
            return '';
        }
    }
};
</script>

<style scoped>
.filter-bar { margin-bottom: 8px; }
.scope-bar {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: 8px;
    vertical-align: middle;
}
.scope-tag {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
}
.tip-alert { margin-bottom: 12px; }
.tbl-scroll-host { flex: 1; min-height: 320px; }
.pager { text-align: right; margin-top: 12px; }
.photo-row { display: flex; align-items: center; gap: 6px; }
.thumb { width: 52px; height: 52px; border-radius: 4px; }
.muted { color: #909399; }
.no-department-tip {
    display: flex; align-items: center; justify-content: center;
    height: 100%; min-height: 280px;
}
.no-department-tip__inner { text-align: center; color: #909399; }
.no-department-tip__inner i { font-size: 48px; margin-bottom: 12px; }
</style>
